import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Clock,
  BarChart3,
  Target,
  Camera,
  Sparkles,
  Lightbulb,
  Zap,
  ChevronRight,
  X
} from 'lucide-react';
import api from '../lib/api';

const TrendsPage = () => {
  const { isExecutive } = useAuth();
  const [trends, setTrends] = useState([]);
  const [summary, setSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [loading, setLoading] = useState(true);
  const [snapshotting, setSnapshotting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [riskAnalysis, setRiskAnalysis] = useState(null);
  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = selectedCompany !== 'all' ? { company_id: selectedCompany } : {};
      
      const [trendsRes, summaryRes, companiesRes] = await Promise.all([
        api.get('/analytics/trends', { params }),
        api.get('/analytics/summary'),
        api.get('/companies')
      ]);
      
      setTrends(trendsRes.data);
      setSummary(summaryRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error('Error fetching trends:', error);
      toast.error('Error al cargar datos de tendencias');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  const handleSnapshot = async () => {
    try {
      setSnapshotting(true);
      await api.post('/analytics/snapshot');
      toast.success('Snapshot de progreso creado');
      fetchData();
    } catch (error) {
      toast.error('Error al crear snapshot');
    } finally {
      setSnapshotting(false);
    }
  };

  const handleAnalyzeRisk = async () => {
    try {
      setAnalyzing(true);
      const response = await api.post('/ai/analyze-risk');
      setRiskAnalysis(response.data);
      if (response.data.analyses?.length > 0) {
        toast.success(`Análisis completado: ${response.data.total_analyzed} KRs analizados`);
      } else {
        toast.info('No hay KRs de alto riesgo para analizar');
      }
    } catch (error) {
      console.error('Error analyzing risk:', error);
      toast.error('Error al analizar riesgo');
    } finally {
      setAnalyzing(false);
    }
  };

  const openAnalysisDetail = (analysis) => {
    setSelectedAnalysis(analysis);
    setShowAnalysisDialog(true);
  };

  const getTrendIcon = (direction) => {
    switch (direction) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-emerald-500" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      default:
        return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  const getRiskBadge = (level) => {
    const styles = {
      high: 'bg-red-100 text-red-700 border-red-200',
      medium: 'bg-amber-100 text-amber-700 border-amber-200',
      low: 'bg-emerald-100 text-emerald-700 border-emerald-200'
    };
    const labels = {
      high: 'Alto Riesgo',
      medium: 'Riesgo Medio',
      low: 'Bajo Riesgo'
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full border ${styles[level]}`}>
        {labels[level]}
      </span>
    );
  };

  const MiniProgressChart = ({ history, currentProgress }) => {
    const maxValue = 100;
    const chartHeight = 40;
    const chartWidth = 120;
    
    // If no history, show just current progress as a single point
    const dataPoints = history.length > 0 
      ? history.map(h => h.value || 0)
      : [currentProgress];
    
    if (dataPoints.length < 2) {
      dataPoints.push(currentProgress);
    }
    
    const points = dataPoints.map((value, index) => {
      const x = (index / (dataPoints.length - 1)) * chartWidth;
      const y = chartHeight - (value / maxValue) * chartHeight;
      return `${x},${y}`;
    }).join(' ');
    
    const lastValue = dataPoints[dataPoints.length - 1];
    const firstValue = dataPoints[0];
    const isUp = lastValue > firstValue;
    
    return (
      <svg width={chartWidth} height={chartHeight} className="overflow-visible">
        <defs>
          <linearGradient id={`gradient-${isUp ? 'up' : 'down'}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={isUp ? '#10b981' : '#ef4444'} stopOpacity="0.3" />
            <stop offset="100%" stopColor={isUp ? '#10b981' : '#ef4444'} stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Area fill */}
        <polygon
          points={`0,${chartHeight} ${points} ${chartWidth},${chartHeight}`}
          fill={`url(#gradient-${isUp ? 'up' : 'down'})`}
        />
        {/* Line */}
        <polyline
          points={points}
          fill="none"
          stroke={isUp ? '#10b981' : '#ef4444'}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Current point */}
        <circle
          cx={chartWidth}
          cy={chartHeight - (lastValue / maxValue) * chartHeight}
          r="3"
          fill={isUp ? '#10b981' : '#ef4444'}
        />
      </svg>
    );
  };

  if (!isExecutive) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
        <BarChart3 className="w-10 h-10 text-gray-300 mx-auto mb-4" />
        <h2 className="text-base font-medium text-gray-900 mb-2">Acceso Restringido</h2>
        <p className="text-sm text-gray-500">Solo Owner y Executive pueden ver las tendencias.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="trends-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-500" />
            Tendencias & Predicciones
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Análisis de progreso y predicción de riesgo para KRs
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger className="w-40 bg-white border-gray-200 text-sm">
              <SelectValue placeholder="Empresa" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {companies.map(c => (
                <SelectItem key={c.company_id} value={c.company_id}>
                  {c.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="default"
            size="sm"
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white border-0"
            onClick={handleAnalyzeRisk}
            disabled={analyzing}
            data-testid="analyze-risk-btn"
          >
            <Sparkles className="w-4 h-4 mr-1.5" />
            {analyzing ? 'Analizando...' : 'Analizar con IA'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-gray-200"
            onClick={handleSnapshot}
            disabled={snapshotting}
            data-testid="snapshot-btn"
          >
            <Camera className="w-4 h-4 mr-1.5" />
            {snapshotting ? 'Guardando...' : 'Snapshot'}
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="border-gray-200"
            onClick={fetchData}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Progreso Promedio</span>
              <Target className="w-4 h-4 text-gray-400" />
            </div>
            <p className="text-2xl font-semibold text-gray-900">{summary.average_progress}%</p>
            <div className="h-1.5 bg-gray-200 rounded-full mt-2 overflow-hidden">
              <div 
                className="h-full bg-blue-500 rounded-full transition-all"
                style={{ width: `${summary.average_progress}%` }}
              />
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">On Track</span>
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            </div>
            <p className="text-2xl font-semibold text-emerald-600">{summary.status_breakdown.on_track}</p>
            <p className="text-xs text-gray-500 mt-1">de {summary.total_krs} KRs</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">En Riesgo</span>
              <Clock className="w-4 h-4 text-amber-500" />
            </div>
            <p className="text-2xl font-semibold text-amber-600">{summary.status_breakdown.at_risk}</p>
            <p className="text-xs text-gray-500 mt-1">requieren atención</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Bloqueados</span>
              <AlertTriangle className="w-4 h-4 text-red-500" />
            </div>
            <p className="text-2xl font-semibold text-red-600">{summary.status_breakdown.blocked}</p>
            <p className="text-xs text-gray-500 mt-1">acción urgente</p>
          </div>
        </div>
      )}

      {/* Company Breakdown */}
      {summary?.company_stats && (
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Progreso por Empresa</h2>
          <div className="grid grid-cols-5 gap-4">
            {summary.company_stats.map(company => (
              <div key={company.code} className="text-center">
                <div className="relative w-16 h-16 mx-auto mb-2">
                  <svg className="w-16 h-16 transform -rotate-90">
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke="#e5e7eb"
                      strokeWidth="4"
                      fill="none"
                    />
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke={company.high_risk_count > 0 ? '#f59e0b' : '#10b981'}
                      strokeWidth="4"
                      fill="none"
                      strokeDasharray={`${(company.avg_progress / 100) * 175.9} 175.9`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-semibold text-gray-900">{Math.round(company.avg_progress)}%</span>
                  </div>
                </div>
                <p className="text-sm font-medium text-gray-900">{company.code}</p>
                <p className="text-xs text-gray-500">{company.total_krs} KRs</p>
                {company.high_risk_count > 0 && (
                  <p className="text-xs text-amber-600 mt-1">{company.high_risk_count} en riesgo</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KR Trends List */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="p-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">Análisis de KRs por Riesgo</h2>
          <p className="text-xs text-gray-500 mt-1">Ordenados por nivel de riesgo y progreso</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-6 h-6 border-2 border-gray-200 border-t-gray-900 rounded-full animate-spin" />
          </div>
        ) : trends.length === 0 ? (
          <div className="p-12 text-center">
            <BarChart3 className="w-10 h-10 text-gray-300 mx-auto mb-4" />
            <p className="text-sm text-gray-500">No hay datos de tendencias disponibles.</p>
            <p className="text-xs text-gray-400 mt-1">Crea un snapshot para comenzar a trackear el progreso.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {trends.map(trend => (
              <div 
                key={trend.kr_id} 
                className="p-4 hover:bg-gray-50 transition-colors"
                data-testid={`trend-${trend.kr_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                        {trend.company_code}
                      </span>
                      {getRiskBadge(trend.risk_level)}
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 truncate" title={trend.kr_title}>
                      {trend.kr_title}
                    </h3>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        {getTrendIcon(trend.trend_direction)}
                        <span className={trend.velocity >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                          {trend.velocity >= 0 ? '+' : ''}{trend.velocity}%/sem
                        </span>
                      </span>
                      {trend.projected_completion_weeks && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          ~{trend.projected_completion_weeks} semanas
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    {/* Mini chart */}
                    <div className="hidden sm:block">
                      <MiniProgressChart 
                        history={trend.history} 
                        currentProgress={trend.progress_percentage}
                      />
                    </div>
                    
                    {/* Progress */}
                    <div className="text-right w-24">
                      <p className="text-lg font-semibold text-gray-900">
                        {trend.progress_percentage}%
                      </p>
                      <p className="text-xs text-gray-500">
                        {trend.current_value}/{trend.target_value} {trend.unit}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Risk Analysis Results */}
      {riskAnalysis?.analyses?.length > 0 && (
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
          <div className="p-4 border-b border-amber-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <h2 className="text-sm font-semibold text-gray-900">Análisis IA de KRs en Riesgo</h2>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRiskAnalysis(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="divide-y divide-amber-200">
            {riskAnalysis.analyses.map((item, index) => (
              <div 
                key={item.kr_id || index}
                className="p-4 hover:bg-amber-100/50 transition-colors cursor-pointer"
                onClick={() => item.analysis && openAnalysisDetail(item)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                        {item.company_code}
                      </span>
                      <span className="text-xs text-gray-500">{item.progress_percentage}%</span>
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 truncate">
                      {item.title}
                    </h3>
                    {item.analysis && (
                      <div className="mt-2 space-y-2">
                        <p className="text-xs text-gray-600 flex items-start gap-2">
                          <Lightbulb className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                          <span>{item.analysis.diagnosis}</span>
                        </p>
                        {item.analysis.quick_wins && (
                          <div className="flex items-center gap-2 text-xs">
                            <Zap className="w-3.5 h-3.5 text-emerald-500" />
                            <span className="text-emerald-700 font-medium">Quick win:</span>
                            <span className="text-gray-600 truncate">{item.analysis.quick_wins[0]}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {item.analysis && (
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        item.analysis.success_probability === 'high' 
                          ? 'bg-emerald-100 text-emerald-700'
                          : item.analysis.success_probability === 'medium'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {item.analysis.success_probability === 'high' ? 'Alta' : item.analysis.success_probability === 'medium' ? 'Media' : 'Baja'} prob.
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analysis Detail Dialog */}
      <Dialog open={showAnalysisDialog} onOpenChange={setShowAnalysisDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Plan de Recuperación IA
            </DialogTitle>
          </DialogHeader>
          
          {selectedAnalysis && selectedAnalysis.analysis && (
            <div className="space-y-6 mt-4">
              {/* KR Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded">
                    {selectedAnalysis.company_code}
                  </span>
                  <span className="text-xs text-gray-500">
                    {selectedAnalysis.progress_percentage}% completado
                  </span>
                </div>
                <h3 className="font-medium text-gray-900">{selectedAnalysis.title}</h3>
              </div>

              {/* Diagnosis */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Diagnóstico
                </h4>
                <p className="text-sm text-gray-600">{selectedAnalysis.analysis.diagnosis}</p>
              </div>

              {/* Root Cause */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Causa Raíz</h4>
                <p className="text-sm text-gray-600 bg-red-50 border border-red-100 rounded-lg p-3">
                  {selectedAnalysis.analysis.root_cause}
                </p>
              </div>

              {/* Quick Wins */}
              {selectedAnalysis.analysis.quick_wins?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-emerald-500" />
                    Quick Wins (48 horas)
                  </h4>
                  <ul className="space-y-2">
                    {selectedAnalysis.analysis.quick_wins.map((win, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600 bg-emerald-50 rounded-lg p-3">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        {win}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recovery Actions */}
              {selectedAnalysis.analysis.recovery_actions?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4 text-blue-500" />
                    Plan de Recuperación
                  </h4>
                  <ol className="space-y-2">
                    {selectedAnalysis.analysis.recovery_actions.map((action, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-gray-600 bg-blue-50 rounded-lg p-3">
                        <span className="w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">
                          {i + 1}
                        </span>
                        {action}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Blockers to Remove */}
              {selectedAnalysis.analysis.blockers_to_remove?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Bloqueos a Eliminar</h4>
                  <ul className="space-y-1">
                    {selectedAnalysis.analysis.blockers_to_remove.map((blocker, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <X className="w-3.5 h-3.5 text-red-500" />
                        {blocker}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recommended Focus */}
              {selectedAnalysis.analysis.recommended_focus && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-amber-800 mb-1">
                    Foco Recomendado
                  </h4>
                  <p className="text-sm text-amber-700">
                    {selectedAnalysis.analysis.recommended_focus}
                  </p>
                </div>
              )}

              {/* Success Probability */}
              <div className="flex items-center justify-between pt-4 border-t">
                <span className="text-sm text-gray-600">Probabilidad de recuperación:</span>
                <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                  selectedAnalysis.analysis.success_probability === 'high' 
                    ? 'bg-emerald-100 text-emerald-700'
                    : selectedAnalysis.analysis.success_probability === 'medium'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {selectedAnalysis.analysis.success_probability === 'high' ? 'Alta' : 
                   selectedAnalysis.analysis.success_probability === 'medium' ? 'Media' : 'Baja'}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrendsPage;
